// Application Detail with Instant Tenant Score
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Grid, 
  Card, 
  Box, 
  Typography, 
  Button, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip
} from "@mui/material";
import { ArrowBack, Download, Schedule } from "@mui/icons-material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import InstantTenantScore from "components/InstantTenantScore";

function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [tenantScore, setTenantScore] = useState(null);

  useEffect(() => {
    // In real app, fetch from API
    // For demo, use dummy data
    setApplication({
      id: "APP-8724",
      name: "Sarah Johnson",
      email: "sarah.j@example.com",
      phone: "(555) 123-4567",
      property: "Sunset Apartments #304",
      rent: 1850,
      monthlyIncome: 7083, // $85,000 annual / 12
      creditScore: 742,
      employmentStatus: "employed",
      evictionHistory: 0,
      criminalRecord: false,
      landlordReference: "positive",
      appliedDate: "May 18, 2025",
      status: "Pending Review",
      documents: [
        { name: "Driver's License", status: "Verified" },
        { name: "Pay Stubs (3 months)", status: "Verified" },
        { name: "Bank Statements", status: "Pending" },
        { name: "References", status: "Verified" }
      ]
    });
  }, [id]);

  if (!application) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3} textAlign="center">
          <Typography>Loading application...</Typography>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        {/* Header */}
        <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <ArgonTypography variant="h4" fontWeight="bold">
              Application {application.id}
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text">
              {application.name} • Applied {application.appliedDate}
            </ArgonTypography>
          </Box>
          <Box display="flex" gap={2}>
            <ArgonButton
              variant="outlined"
              color="primary"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/tenants/applications")}
            >
              Back
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="success"
              onClick={async () => {
                if (window.confirm("Approve this application?")) {
                  // Will update Firestore when wired
                  navigate("/tenants/applications");
                }
              }}
            >
              Approve
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="error"
              onClick={async () => {
                if (window.confirm("Reject this application?")) {
                  navigate("/tenants/applications");
                }
              }}
            >
              Reject
            </ArgonButton>
          </Box>
        </ArgonBox>

        <Grid container spacing={3}>
          {/* Applicant Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Applicant Information
                </ArgonTypography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="caption" color="text">
                      Full Name
                    </ArgonTypography>
                    <ArgonTypography variant="body1" fontWeight="medium">
                      {application.name}
                    </ArgonTypography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="caption" color="text">
                      Email
                    </ArgonTypography>
                    <ArgonTypography variant="body1" fontWeight="medium">
                      {application.email}
                    </ArgonTypography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="caption" color="text">
                      Phone
                    </ArgonTypography>
                    <ArgonTypography variant="body1" fontWeight="medium">
                      {application.phone}
                    </ArgonTypography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="caption" color="text">
                      Monthly Income
                    </ArgonTypography>
                    <ArgonTypography variant="body1" fontWeight="medium">
                      ${application.monthlyIncome.toLocaleString()}
                    </ArgonTypography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Property Details
                </ArgonTypography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="caption" color="text">
                      Property
                    </ArgonTypography>
                    <ArgonTypography variant="body1" fontWeight="medium">
                      {application.property}
                    </ArgonTypography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <ArgonTypography variant="caption" color="text">
                      Monthly Rent
                    </ArgonTypography>
                    <ArgonTypography variant="body1" fontWeight="medium">
                      ${application.rent}/month
                    </ArgonTypography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Documents
                </ArgonTypography>
                
                <List>
                  {application.documents.map((doc, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText primary={doc.name} />
                      <Chip 
                        label={doc.status} 
                        size="small"
                        color={doc.status === "Verified" ? "success" : "warning"}
                      />
                    </ListItem>
                  ))}
                </List>
              </ArgonBox>
            </Card>
          </Grid>

          {/* Instant Tenant Score */}
          <Grid item xs={12} md={4}>
            <InstantTenantScore
              onScoreCalculated={setTenantScore}
              initialData={{
                credit_score: application.creditScore,
                monthly_income: application.monthlyIncome,
                requested_rent: application.rent,
                employment_status: application.employmentStatus,
                eviction_history: application.evictionHistory,
                criminal_record: application.criminalRecord,
                landlord_reference: application.landlordReference
              }}
            />

            {/* Quick Actions */}
            <Card sx={{ mt: 3, p: 2 }}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                Quick Actions
              </ArgonTypography>
              
              <ArgonButton 
                variant="outlined" 
                fullWidth 
                startIcon={<Schedule />}
                sx={{ mb: 1 }}
              >
                Schedule Interview
              </ArgonButton>
              
              <ArgonButton 
                variant="outlined" 
                fullWidth 
                startIcon={<Download />}
              >
                Download Application
              </ArgonButton>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ApplicationDetail;